import { useState }          from 'react'
import { Outlet, NavLink, useNavigate }   from 'react-router-dom'
import {
  LayoutDashboard, Megaphone, Users, Kanban,
  FileText, MessageCircle, Mail, BarChart3,
  Settings, Building2, Zap, ChevronLeft,
  ChevronRight, Bell, LogOut, Crown, Workflow,
} from 'lucide-react'

import { useAuthStore }       from '@/stores/authStore'
import { useWhiteLabelStore } from '@/stores/whiteLabelStore'
import { clsx }               from 'clsx'

const NAV_ITEMS = [
  { to: '/dashboard',    icon: LayoutDashboard, label: 'Início' },
  { to: '/campanhas',    icon: Megaphone,        label: 'Campanhas' },
  { to: '/leads',        icon: Users,            label: 'Leads' },
  { to: '/crm',          icon: Kanban,           label: 'CRM' },
  { to: '/landing-pages',icon: FileText,         label: 'Landing Pages' },
  { to: '/whatsapp',     icon: MessageCircle,    label: 'WhatsApp' },
  { to: '/funis',        icon: Workflow,         label: 'Funis' },
  { to: '/email',        icon: Mail,             label: 'E-mail' },
  { to: '/relatorios',   icon: BarChart3,        label: 'Relatórios' },
]

const AGENCY_ITEMS = [
  { to: '/agencia',             icon: Building2, label: 'Clientes' },
  { to: '/agencia/white-label', icon: Zap,       label: 'White-label' },
]

export default function AppLayout() {
  const [collapsed, setCollapsed] = useState(false)
  const { profile, workspace, logout } = useAuthStore()
  const { config, isWhiteLabel }       = useWhiteLabelStore()
  const navigate = useNavigate()

  const isAgency = profile?.plan === 'agency' || profile?.plan === 'agency_pro'
  const brandName = isWhiteLabel ? config?.brandName : 'Dikis AI'
  const logoUrl   = isWhiteLabel ? config?.logoUrl   : null

  return (
    <div className="flex h-screen overflow-hidden bg-dark-900">

      {/* ── Sidebar ─────────────────────────────────────── */}
      <aside
        className={clsx(
          'flex flex-col border-r border-dark-700 bg-dark-900',
          'transition-all duration-300 ease-in-out flex-shrink-0',
          collapsed ? 'w-16' : 'w-60',
        )}
      >
        {/* Logo */}
        <div className="flex items-center h-16 px-4 border-b border-dark-700">
          {logoUrl ? (
            <img src={logoUrl} alt={brandName} className="h-8 w-auto" />
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-brand flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-sm">D</span>
              </div>
              {!collapsed && (
                <span className="font-bold text-white text-sm truncate">
                  {brandName}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Nav principal */}
        <nav className="flex-1 py-4 px-2 space-y-0.5 overflow-y-auto scrollbar-hidden">
          {NAV_ITEMS.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                clsx('sidebar-item', isActive && 'active')
              }
              title={collapsed ? label : undefined}
            >
              <Icon size={18} className="flex-shrink-0" />
              {!collapsed && <span className="truncate">{label}</span>}
            </NavLink>
          ))}

          {/* Seção Agency */}
          {isAgency && (
            <>
              <div className={clsx('pt-4 pb-1', !collapsed && 'px-2')}>
                {!collapsed && (
                  <p className="text-xs font-semibold text-dark-500 uppercase tracking-wider">
                    Agência
                  </p>
                )}
              </div>
              {AGENCY_ITEMS.map(({ to, icon: Icon, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    clsx('sidebar-item', isActive && 'active')
                  }
                  title={collapsed ? label : undefined}
                >
                  <Icon size={18} className="flex-shrink-0" />
                  {!collapsed && <span className="truncate">{label}</span>}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        {/* Rodapé da sidebar */}
        <div className="border-t border-dark-700 p-2 space-y-0.5">
          <NavLink
            to="/configuracoes"
            className={({ isActive }) =>
              clsx('sidebar-item', isActive && 'active')
            }
          >
            <Settings size={18} className="flex-shrink-0" />
            {!collapsed && <span>Configurações</span>}
          </NavLink>

          <button
            onClick={() => logout().then(() => navigate('/login'))}
            className="sidebar-item w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
          >
            <LogOut size={18} className="flex-shrink-0" />
            {!collapsed && <span>Sair</span>}
          </button>
        </div>

        {/* Toggle collapse */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute top-[4.5rem] -right-3 w-6 h-6 rounded-full
                     bg-dark-700 border border-dark-600 flex items-center justify-center
                     hover:bg-dark-600 transition-colors z-10"
        >
          {collapsed
            ? <ChevronRight size={12} className="text-dark-300" />
            : <ChevronLeft  size={12} className="text-dark-300" />
          }
        </button>
      </aside>

      {/* ── Conteúdo principal ──────────────────────────── */}
      <div className="flex flex-col flex-1 overflow-hidden">

        {/* Header */}
        <header className="h-16 flex items-center justify-between px-6
                           border-b border-dark-700 bg-dark-900 flex-shrink-0">
          {/* Workspace name */}
          <div>
            <h2 className="text-sm font-semibold text-dark-100 truncate max-w-xs">
              {workspace?.name}
            </h2>
            <p className="text-xs text-dark-400 capitalize">
              {profile?.plan?.replace('_', ' ')}
            </p>
          </div>

          {/* Ações do header */}
          <div className="flex items-center gap-3">
            {/* Botão de upgrade para planos inferiores */}
            {(profile?.plan === 'starter') && (
              <button
                onClick={() => navigate('/upgrade')}
                className="btn-primary text-xs px-3 py-1.5 gap-1.5"
              >
                <Crown size={13} />
                Fazer upgrade
              </button>
            )}

            {/* Notificações */}
            <button className="relative w-9 h-9 flex items-center justify-center
                               rounded-xl hover:bg-dark-700 transition-colors">
              <Bell size={18} className="text-dark-300" />
              {/* Badge de notificação */}
              <span className="absolute top-1.5 right-1.5 w-2 h-2
                               bg-brand-500 rounded-full" />
            </button>

            {/* Avatar */}
            <div className="w-9 h-9 rounded-xl bg-gradient-brand
                            flex items-center justify-center cursor-pointer"
                 onClick={() => navigate('/configuracoes')}>
              <span className="text-white text-sm font-bold">
                {profile?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
          </div>
        </header>

        {/* Página */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}